import { useEffect, useState } from "react"
import { Card, Flex, Form, InputNumber, Switch, Tooltip } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import { useWizardContext } from "../../../WizardContext"
import styles from "./BasePrices.module.css"

const isPriceInvalid = (priceEntry, isEnabled) =>
  isEnabled && priceEntry.price !== null && priceEntry.price !== undefined && priceEntry.price <= 0

const BasePrices = () => {
  const { t } = useTranslation()
  const { form, trip } = useWizardContext()

  // Reactively tracks ticket_types as the user adds/removes them in TicketTypeStep.
  // preserve — the field has no Form.Item, and useWatch skips unregistered fields without it.
  // Fall back to Redux trip data at render time when the form hasn't been initialized yet
  // (stale Redux cache race — WizardProvider initializes once and the guard blocks re-init).
  const watchedTypes = Form.useWatch("ticket_types", { form, preserve: true })
  const ticketTypes = ((watchedTypes?.length ? watchedTypes : trip?.ticket_types) || []).filter((ticket) => !ticket._destroy)

  const [ticketPrices, setTicketPrices] = useState(
    () => form.getFieldValue("ticketTypePrices") || {}
  )

  // Keep form in sync so buildPricingPayload can read ticketTypePrices
  useEffect(() => {
    form.setFieldValue("ticketTypePrices", ticketPrices)
  }, [ticketPrices, form])

  // Seed prices from Redux when form wasn't initialized with ticket_type prices (stale cache race).
  useEffect(() => {
    if (Object.keys(ticketPrices).length > 0 || !trip?.ticket_types?.length) return
    setTicketPrices(
      Object.fromEntries(
        trip.ticket_types.map((t) => [t.id, { enabled: t.enabled !== false, price: t.price > 0 ? t.price : null }])
      )
    )
  }, [trip?.ticket_types])

  // ticket_types[].enabled is the single source of truth for the toggle — shared
  // with the Ticket Type tab so both stay in sync. ticketTypePrices only mirrors
  // it for the pricing payload.
  const enabledCount = ticketTypes.filter((ticket) => ticket.enabled !== false).length

  const handleToggle = (id, checked) => {
    if (!checked && enabledCount <= 1) return
    setTicketPrices((prev) => ({ ...prev, [id]: { ...prev[id], enabled: checked } }))
    const currentTypes = form.getFieldValue("ticket_types")
    const baseTypes = currentTypes?.length ? currentTypes : ticketTypes
    form.setFieldValue(
      "ticket_types",
      baseTypes.map((ticket) => (ticket.id === id ? { ...ticket, enabled: checked } : ticket))
    )
  }

  const handlePriceChange = (id, value) =>
    setTicketPrices((prev) => ({ ...prev, [id]: { ...prev[id], price: value } }))

  if (ticketTypes.length === 0) {
    return (
      <Card>
        <Text fw={600} size="xl">{t("experiences.pricing.base_prices.title")}</Text>
        <Flex align="center" gap={8} className="mt-m">
          <InfoCircleOutlined className={styles.infoIcon} />
          <Text fw={500}>{t("experiences.pricing.base_prices.no_ticket_types_hint")}</Text>
        </Flex>
      </Card>
    )
  }

  return (
    <Card>
      <Text fw={600} size="xl">{t("experiences.pricing.base_prices.title")}</Text>
      <Flex vertical gap={4} className="mt-m">
        {ticketTypes.map((ticket) => {
          const priceEntry = ticketPrices[ticket.id] || {}
          const isEnabled = ticket.enabled !== false

          const isInvalid = isPriceInvalid(priceEntry, isEnabled)
          const isLastEnabled = isEnabled && enabledCount <= 1

          return (
            <Flex key={ticket.id} vertical gap={2}>
              <Flex align="center" gap={8} className={styles.ticketRow}>
                <Flex align="center" gap={12} className={styles.ticketLeft}>
                  <Tooltip title={isLastEnabled ? t("experiences.pricing.base_prices.last_enabled_tooltip") : ""}>
                    {/* span keeps the tooltip working — disabled switches swallow mouse events */}
                    <span>
                      <Switch checked={isEnabled} disabled={isLastEnabled} onChange={(checked) => handleToggle(ticket.id, checked)} />
                    </span>
                  </Tooltip>
                  <Flex align="center" gap={8} flex={1}>
                    <Text className={styles.ticketLabel}>{ticket.title}</Text>
                    {ticket.description && (
                      <Tooltip title={ticket.description}>
                        <InfoCircleOutlined className={styles.infoIcon} />
                      </Tooltip>
                    )}
                  </Flex>
                </Flex>
                <InputNumber
                  value={priceEntry.price ?? null}
                  onChange={(val) => handlePriceChange(ticket.id, val)}
                  status={isInvalid ? "error" : undefined}
                  prefix="$"
                  min={0}
                  precision={2}
                  placeholder="0.00"
                  className={styles.priceInput}
                  size="large"
                />
              </Flex>
              {isInvalid && (
                <Text size="s" className={styles.errorText}>{t("experiences.pricing.base_prices.price_must_be_positive")}</Text>
              )}
            </Flex>
          )
        })}
      </Flex>
    </Card>
  )
}

export default BasePrices
